import * as React from 'react'
import { observer } from 'mobx-react'

import {
  Form,
  FormGroupContainer,
  FormRow,
  TextAreaContainer,
  TextInputContainer,
} from 'components/inputs'
import {
  CustomSelect,
  ItemSource,
  RenderItemProps,
  RenderSearchItemProps,
  SingleItemSelectedDefault,
  useCustomSelectStateFromItemSource,
} from 'components/inputs/CustomSelect'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from 'components/ModalSheet'

import QuestionIcon from 'jobs/components/QuestionIcon'
import SearchResultItem from 'components/inputs/CustomSelect/SearchResultItem'
import Button from 'components/Button'
import ErrorBlock from 'components/ErrorBlock'

import { PromptType } from 'jobs/prompts/prompt-types'
import CreatePromptDialogViewModel, {
  IPromptOption,
} from 'jobs/prompts/creation/CreatePromptDialogViewModel'

import { match } from 'utils/pattern-match-util'

import styles from './CreatePromptDialog.sass'
import DocumentUploadInputEditor from './../../Documents/index'
interface Props {
  vm: CreatePromptDialogViewModel
}

export default observer(function CreatePromptDialog({ vm }: Props) {
  const itemSource = React.useMemo(
    () =>
      ItemSource.static(vm.options, (item, text) => {
        return item.displayName.toLowerCase().includes(text.toLowerCase())
      }),
    [vm.options]
  )
  const selectProps = useCustomSelectStateFromItemSource({ itemSource })

  return (
    <ModalSheet
      showing={vm.showing}
      onRequestClose={vm.sendPrompt.pending ? undefined : vm.close}
      disableCloseOnOutsideClick
    >
      <ModalSheetHeader withCloseButton></ModalSheetHeader>
      <ModalSheetContent>
        <ModalSheetContentBlock>
          <ModalSheetContentTitle>Send Prompt</ModalSheetContentTitle>
          <ModalSheetContentDescription>
            Send a prompt
          </ModalSheetContentDescription>
        </ModalSheetContentBlock>

        <ModalSheetContentBlock>
          <Form>
            <FormRow>
              <FormGroupContainer
                model={vm}
                field={'option'}
                label="Prompt type"
              >
                <CustomSelect
                  {...selectProps}
                  data-test="prompt-type-dropdown"
                  value={vm.option}
                  itemToString={(i) => i.displayName}
                  multi={false}
                  onChange={vm.setPromptOption}
                  renderItem={(itemProps) => (
                    <SelectedPrompt itemProps={itemProps} />
                  )}
                  renderSearchItem={(itemProps) => (
                    <PromptSearchItem {...itemProps} />
                  )}
                />
              </FormGroupContainer>
            </FormRow>
            <FormRow>
              <TextInputContainer
                model={vm}
                field="title"
                name="title"
                label="Title"
                placeholder="The title of your prompt."
                disabled={false}
                error={vm.validation.getError('title')}
                data-test="prompt-title-input"
                help={vm.charsRemainingIndicators.title}
              />
            </FormRow>
            <FormRow>
              <TextAreaContainer
                model={vm}
                field="description"
                name="description"
                label="Description"
                placeholder="The description for your prompt."
                disabled={false}
                error={vm.validation.getError('description')}
                data-test="prompt-description-input"
                help={vm.charsRemainingIndicators.description}
              />
            </FormRow>
            {match(vm.promptType, {
              [PromptType.Bool]: () => null,
              [PromptType.Text]: () => null,
              [PromptType.DocumentUpload]: () => (
                <DocumentUploadInputEditor
                  vm={vm.detailsViewModels[PromptType.DocumentUpload]}
                />
              ),
            })}
          </Form>
        </ModalSheetContentBlock>
      </ModalSheetContent>
      {vm.sendPrompt.rejected && (
        <ErrorBlock tackOn="bottom" error={vm.sendPrompt.error} />
      )}
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            type="default"
            onClick={vm.close}
            dataTest="prompt-close-button"
          >
            Close
          </Button>
          <Button
            loading={vm.sendPrompt.pending}
            type="primary"
            onClick={vm.sendPrompt}
            dataTest="prompt-submit-button"
          >
            Send Prompt
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})

interface SelectedPromptProps {
  itemProps: RenderItemProps<IPromptOption>
}

function SelectedPrompt({ itemProps }: SelectedPromptProps) {
  return (
    <div className={styles.itemContainer}>
      <SingleItemSelectedDefault
        visual={
          <QuestionIcon
            className={styles.promptDefaultItemQuestionIcon}
            size={32}
            type={itemProps.item.promptType}
          />
        }
        {...itemProps}
      />
    </div>
  )
}

interface PromptSearchItem extends RenderSearchItemProps<IPromptOption> {}

const PromptSearchItem = React.forwardRef(function PromptSearchItem(
  { item, itemToString, ...itemProps }: PromptSearchItem,
  ref: React.Ref<any>
) {
  return (
    <SearchResultItem
      ref={ref}
      {...itemProps}
      className={styles.promptSearchResultItem}
      visual={
        <QuestionIcon
          className={styles.promptSearchResultQuestionIcon}
          size={32}
          type={item.promptType}
        />
      }
      data-test={'prompt-type-option-' + item.promptType.toLowerCase()}
    >
      {item.displayName}
    </SearchResultItem>
  )
})
